import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  ClipboardCheck,
  FileStack,
  Plus,
  Sparkles,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/context/auth'
import {
  type Case,
  formatDate,
  formatUSD,
} from '@/lib/cases'
import { TrackerWorkspacePanel } from '@/features/case-workspace/WorkspaceShared'
import { StatusBadge } from '@/components/app/StatusBadge'
import { Sparkline, DonutChart } from '@/components/app/charts'
import { EmptyState } from '@/components/app/EmptyState'

const SPARK_DAYS = 14

type ActionQueueTone = 'coral' | 'sunset' | 'secondary'

type ActionQueueCardProps = {
  title: string
  count: number
  tone: ActionQueueTone
  ctaLabel: string
  ctaTo: string
}

type WorkspaceCaseCardProps = {
  kase: Case
  reason: string
  actionLabel: string
}

export function Dashboard() {
  const { user } = useAuth()
  const [cases, setCases] = useState<Case[]>([])
  const [loading, setLoading] = useState(true)
  const seededRef = useRef(false)

  async function fetchCases(userId: string) {
    const { data } = await supabase
      .from('cases')
      .select('*')
      .eq('agent_id', userId)
      .order('updated_at', { ascending: false })
    return (data ?? []) as Case[]
  }

  useEffect(() => {
    if (!user) return

    let cancelled = false

    void fetchCases(user.id).then((data) => {
      if (cancelled) return
      setCases(data)
      setLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [user])

  useEffect(() => {
    if (!user || loading || seededRef.current || cases.length > 0) return
    let cancelled = false

    seededRef.current = true
    ;(async () => {
      const { data, error } = await supabase.rpc('seed_demo_cases_for_current_user')
      if (error || (data ?? 0) <= 0 || cancelled) return

      const refreshedCases = await fetchCases(user.id)
      if (cancelled) return
      setCases(refreshedCases)
    })()

    return () => {
      cancelled = true
    }
  }, [user, loading, cases.length])

  const totalValue = useMemo(
    () => cases.reduce((sum, kase) => sum + Number(kase.total_value_usd ?? 0), 0),
    [cases],
  )

  const sparkData = useMemo(() => {
    const buckets: Record<string, number> = {}
    const today = new Date()
    for (let i = SPARK_DAYS - 1; i >= 0; i--) {
      const d = new Date(today)
      d.setDate(today.getDate() - i)
      buckets[d.toISOString().slice(0, 10)] = 0
    }
    cases.forEach((kase) => {
      const day = kase.created_at.slice(0, 10)
      if (day in buckets) buckets[day] += 1
    })
    return Object.values(buckets)
  }, [cases])

  const statusMix = useMemo(
    () =>
      [
        { label: 'New', value: cases.filter((kase) => kase.status === 'draft').length },
        { label: 'Intake', value: cases.filter((kase) => kase.status === 'intake').length },
        { label: 'In Review', value: cases.filter((kase) => kase.status === 'review').length },
        { label: 'Ready', value: cases.filter((kase) => kase.status === 'ready').length },
      ].filter((item) => item.value > 0),
    [cases],
  )

  const flaggedCases = useMemo(
    () =>
      [...cases]
        .filter((kase) => (kase.flagged_count ?? 0) > 0)
        .sort((a, b) => {
          const flagDelta = (b.flagged_count ?? 0) - (a.flagged_count ?? 0)
          if (flagDelta !== 0) return flagDelta
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        }),
    [cases],
  )

  const readyCases = useMemo(
    () =>
      [...cases]
        .filter((kase) => kase.status === 'ready')
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()),
    [cases],
  )

  const intakeCases = useMemo(
    () =>
      [...cases]
        .filter((kase) => kase.status === 'intake')
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()),
    [cases],
  )

  const myCases = useMemo(
    () =>
      [...cases]
        .filter((kase) => kase.assigned_agent_email?.trim() === user?.email)
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()),
    [cases, user?.email],
  )

  const priorityCases = useMemo(() => {
    const seen = new Set<string>()
    const nextCases: Array<{ kase: Case; reason: string; actionLabel: string }> = []

    readyCases.slice(0, 2).forEach((kase) => {
      if (seen.has(kase.id)) return
      seen.add(kase.id)
      nextCases.push({
        kase,
        reason: 'This entry is ready for final review and transmission prep.',
        actionLabel: 'Prepare transmit',
      })
    })

    flaggedCases.slice(0, 3).forEach((kase) => {
      if (seen.has(kase.id)) return
      seen.add(kase.id)
      nextCases.push({
        kase,
        reason: `${kase.flagged_count} flag${kase.flagged_count === 1 ? '' : 's'} need attention before the case can move smoothly forward.`,
        actionLabel: 'Resolve flags',
      })
    })

    intakeCases.slice(0, 2).forEach((kase) => {
      if (seen.has(kase.id)) return
      seen.add(kase.id)
      nextCases.push({
        kase,
        reason: 'This case still needs intake or document workspace progress.',
        actionLabel: 'Continue intake',
      })
    })

    return nextCases.slice(0, 6)
  }, [flaggedCases, readyCases, intakeCases])

  return (
    <section className="space-y-6">
      <header>
        <div>
          <h1 className="text-3xl font-bold text-primary">Dashboard</h1>
          <p className="mt-1 text-sm text-neutral-600">
            Welcome back, {user?.user_metadata?.full_name || user?.email}.
          </p>
        </div>
      </header>

      <TrackerWorkspacePanel
        eyebrow="Agent workspace"
        title="Today’s casework"
        helpText="Use this workspace to spot the cases that need attention first, move ready entries toward transmission, and keep intake work moving without hunting through reports."
        body={
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <WorkspaceQueueTile
              title="My Cases"
              count={loading ? 0 : myCases.length}
              tone="secondary"
              ctaLabel="Open assigned cases"
              ctaTo="/cases?assignment=mine"
            />
            <WorkspaceQueueTile
              title="Flags to resolve"
              count={loading ? 0 : flaggedCases.length}
              tone="coral"
              ctaLabel="Work flagged cases"
              ctaTo="/cases?queue=flagged"
            />
            <WorkspaceQueueTile
              title="Ready to transmit"
              count={loading ? 0 : readyCases.length}
              tone="sunset"
              ctaLabel="Review ready entries"
              ctaTo="/cases?queue=ready"
            />
            <WorkspaceQueueTile
              title="Intake in progress"
              count={loading ? 0 : intakeCases.length}
              tone="secondary"
              ctaLabel="Continue intake"
              ctaTo="/cases?queue=intake"
            />
          </div>
        }
        summaryTiles={[]}
      />

      <div className="grid gap-6 xl:grid-cols-[1.9fr_1fr]">
        <section className="space-y-4 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-bold text-primary">Cases to pick up now</h2>
            </div>
            <Link
              to="/cases"
              className="inline-flex items-center gap-1 text-sm font-semibold text-secondary hover:text-primary"
            >
              Open cases <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </div>

          <div className="rounded-2xl border border-neutral-200 bg-neutral-50/70 p-4">
            {loading ? (
              <div className="rounded-lg border border-dashed border-neutral-300 bg-white px-4 py-6 text-center text-sm text-neutral-500">
                Loading workspace queue…
              </div>
            ) : priorityCases.length === 0 ? (
              <div>
                <EmptyState
                  Icon={ClipboardCheck}
                  title="No active queue yet"
                  body="Create a case or continue intake work to start building your agent workspace queue."
                  action={
                    <Link
                      to="/cases/new"
                      className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2.5 font-semibold text-white hover:bg-primary-dark"
                    >
                      <Plus size={16} aria-hidden="true" />
                      Create first case
                    </Link>
                  }
                />
              </div>
            ) : (
              <div className="grid gap-3">
                {priorityCases.map(({ kase, reason, actionLabel }) => (
                  <WorkspaceCaseCard
                    key={kase.id}
                    kase={kase}
                    reason={reason}
                    actionLabel={actionLabel}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        <div className="space-y-4">
          <section className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
            <h2 className="text-base font-bold text-primary">Quick actions</h2>
            <p className="mt-1 text-sm text-neutral-600">
              Jump straight into the most common agent tasks.
            </p>
            <div className="mt-4 grid gap-3">
              <QuickActionLink
                to="/cases/new"
                icon={<Plus size={16} aria-hidden="true" />}
                title="Start a new case"
                detail="Create an entry and move directly into the document workspace."
              />
              <QuickActionLink
                to="/cases"
                icon={<FileStack size={16} aria-hidden="true" />}
                title="Search all cases"
                detail="Find cases by title, importer, port, or status filters."
              />
              <QuickActionLink
                to="/cases"
                icon={<Sparkles size={16} aria-hidden="true" />}
                title="Open review-ready work"
                detail="Pick up cases that are moving toward AWA review or transmission."
              />
            </div>
          </section>

          <section className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
            <div className="flex items-end justify-between gap-4">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-500">
                  New cases — last 14 days
                </h2>
                <p className="mt-1 text-2xl font-bold text-primary">
                  {sparkData.reduce((sum, value) => sum + value, 0)} created
                </p>
              </div>
              <div className="text-right text-xs text-neutral-500">
                Total entered value
                <div className="mt-1 text-base font-semibold text-primary">{formatUSD(totalValue)}</div>
              </div>
            </div>
            <Sparkline data={sparkData} width={420} height={84} className="mt-4 h-24 w-full" />
          </section>

          <section className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-neutral-500 mb-4">
              Active status mix
            </h2>
            {statusMix.length === 0 ? (
              <p className="text-xs text-neutral-500">Create a case to see active case distribution.</p>
            ) : (
              <DonutChart data={statusMix} size={170} thickness={24} />
            )}
          </section>
        </div>
      </div>
    </section>
  )
}

function WorkspaceQueueTile({
  title,
  count,
  tone,
  ctaLabel,
  ctaTo,
}: ActionQueueCardProps) {
  const toneClassName = {
    coral: 'bg-accent-coral',
    sunset: 'bg-accent-sunset',
    secondary: 'bg-[#89a7cb]',
  }[tone]

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-white">{title}</div>
          <div className="mt-2 text-4xl font-bold text-white">{count}</div>
        </div>
        <span
          className={`mt-1 inline-flex h-3.5 w-3.5 flex-shrink-0 rounded-full ${toneClassName}`}
          aria-hidden="true"
        />
      </div>
      <Link
        to={ctaTo}
        className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#9fc3f4] hover:text-white"
      >
        {ctaLabel} <ArrowRight size={14} aria-hidden="true" />
      </Link>
    </div>
  )
}

function WorkspaceCaseCard({ kase, reason, actionLabel }: WorkspaceCaseCardProps) {
  return (
    <Link
      to={`/cases/${kase.id}`}
      className="rounded-2xl border border-neutral-200 bg-white px-4 py-4 transition hover:border-secondary/30 hover:shadow-sm focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="text-xs font-mono text-neutral-500">{kase.case_number}</div>
          <div className="mt-1 text-base font-semibold text-primary">{kase.title}</div>
          <p className="mt-2 text-sm text-neutral-600">{reason}</p>
        </div>
        <div className="flex flex-col items-start gap-2 lg:items-end">
          <StatusBadge status={kase.status} />
          <div className="text-sm font-semibold text-primary">{formatUSD(kase.total_value_usd)}</div>
          <div className="text-xs text-neutral-500">Updated {formatDate(kase.updated_at)}</div>
        </div>
      </div>
      <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-secondary">
        {actionLabel} <ArrowRight size={14} aria-hidden="true" />
      </div>
    </Link>
  )
}

function QuickActionLink({
  to,
  icon,
  title,
  detail,
}: {
  to: string
  icon: React.ReactNode
  title: string
  detail: string
}) {
  return (
    <Link
      to={to}
      className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 transition hover:border-secondary/25 hover:bg-white hover:shadow-sm focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
    >
      <span className="mt-0.5 inline-flex rounded-md bg-secondary-lightest p-2 text-secondary">{icon}</span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-primary">{title}</span>
        <span className="mt-1 block text-sm text-neutral-600">{detail}</span>
      </span>
    </Link>
  )
}
